import { toNumber } from 'lodash-es'
import { isTruthy } from './isTruthy'
import { DomesticPropertyRatePlan } from '~/api/routes/domestic/order/controller/utils'
import {
  RatePlanBenefitListType,
  TRatePlanBenefitListType,
} from '~/api/services/rate-plan-api/index.type'
import { ProductConfigControlParking } from '~/api/services/product-config/index.type'

enum DomesticRatePlanBenefitTypeText {
  EARLY_CHECK_IN = '얼리체크인',
  LATE_CHECK_IN = '레이트체크인',
  EARLY_CHECK_OUT = '얼리체크아웃',
  LATE_CHECK_OUT = '레이트체크아웃',
  FREE_BREAKFAST = '조식제공',
  FREE_BREAKFAST_NUMBER = '조식제공(N인)',
}

export type DomesticRatePlanOptionParams = Pick<DomesticPropertyRatePlan, 'benefitList' | 'config'>

/**
 * @desc Rateplan 요금제 안내 (체크인/체크아웃/조식제공)
 * @link AC: https://yanolja.atlassian.net/wiki/spaces/FES/pages/103767758/2023-11-08+FES+-+RatePlan+1+N+Phase+1
 * @link API: https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/90826357/rateplan+1+N
 * @link 구현스펙: https://yanolja.atlassian.net/wiki/spaces/FES/pages/103769015/Rate+Plan+1+N+util
 *
 * @param benefitList
 * @return { checkIn, checkOut, breakfast }
 */
export const getDomesticRateplanBenefits = (
  benefitList: DomesticRatePlanOptionParams['benefitList']
) => {
  if (!benefitList) {
    return {}
  }

  // value(string)가 true 이거나 number(0 이상)인 것만 필터링
  const benefitsType = benefitList
    .filter(benefit => benefit.value === 'true' || !!toNumber(benefit.value))
    .map(benefit => benefit.type)

  const isIncludedType = (type: TRatePlanBenefitListType) => benefitsType.includes(type)

  const getCheckInOutText = ({
    earlyType,
    lateType,
  }: {
    earlyType: TRatePlanBenefitListType
    lateType: TRatePlanBenefitListType
  }) => {
    if (isIncludedType(earlyType)) {
      return DomesticRatePlanBenefitTypeText[earlyType]
    }
    if (isIncludedType(lateType)) {
      return DomesticRatePlanBenefitTypeText[lateType]
    }
    return ''
  }

  const checkIn = getCheckInOutText({
    earlyType: RatePlanBenefitListType.EARLY_CHECK_IN,
    lateType: RatePlanBenefitListType.LATE_CHECK_IN,
  })

  const checkOut = getCheckInOutText({
    earlyType: RatePlanBenefitListType.EARLY_CHECK_OUT,
    lateType: RatePlanBenefitListType.LATE_CHECK_OUT,
  })

  const breakfastNumber =
    benefitList.find(benefit => benefit.type === RatePlanBenefitListType.FREE_BREAKFAST_NUMBER)
      ?.value ?? 0

  const breakfast =
    isIncludedType(RatePlanBenefitListType.FREE_BREAKFAST_NUMBER) && breakfastNumber
      ? DomesticRatePlanBenefitTypeText[RatePlanBenefitListType.FREE_BREAKFAST_NUMBER].replace(
          'N',
          breakfastNumber
        )
      : isIncludedType(RatePlanBenefitListType.FREE_BREAKFAST)
        ? DomesticRatePlanBenefitTypeText[RatePlanBenefitListType.FREE_BREAKFAST]
        : ''

  return {
    checkIn,
    checkOut,
    breakfast,
  }
}

/**
 * @summary pre order 주차 가능 여부 조회
 * AC: {@link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29231156/RatePlan+1+N+Phase+1+Front}
 *
 * API: {@link https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/89832775/Configuration+API+-+-}
 *
 * spec: {@link https://yanolja.atlassian.net/wiki/spaces/FES/pages/103745295/-}
 *
 * @test test/app/utils/getRatePlanParkingInfo.spec.ts
 *
 * @params {RatePlanConfig} config from rate-plan-config API
 *
 * @returns {{isAvailableParking: boolean, visitTypeText: string}}
 * isAvailableParking: 주차 가능 여부
 *
 * visitTypeText: 주문서 > 방문수단 노출 text
 */
export const getRatePlanParkingInfo = (config: DomesticRatePlanOptionParams['config']) => {
  const WALKING_ONLY_TEXT = '주차장을 이용할 수 없는 객실이에요'
  const NO_PARKING_LOG_TEXT = '주차장이 없는 숙소라 주차를 할 수 없어요'
  const FULL_PARKING_LOG_TEXT = '주차장이 만차여서 주차를 할 수 없어요'

  const controlParking = config.control.parking
  const isAvailableParking =
    controlParking === ProductConfigControlParking.PARKING_AVAILABLE && !config.parking?.full
  const visitTypeText =
    controlParking === ProductConfigControlParking.WALKING_ONLY
      ? WALKING_ONLY_TEXT
      : controlParking === ProductConfigControlParking.NO_PARKING_LOT
        ? NO_PARKING_LOG_TEXT
        : !isAvailableParking
          ? FULL_PARKING_LOG_TEXT
          : ''

  return {
    isAvailableParking,
    visitTypeText,
  }
}

/**
 * @summary Rateplan 요금제 옵션 안내 (체크인/체크아웃/조식제공)
 * @desc Pre-Order (장바구니, 주문서) 에서 사용되는 공통함수
 * @readme !주의! 수정시 app/utils/domesticRateplan.spec.ts 도 수정 필요
 * @param benefitList
 * @param config
 * @param isOrderPage 주문서에서는 주차 불가 여부를 노출하지 않음
 * @return {array}
 */
export const getPreOrderRatePlanOptions = ({
  benefitList,
  config,
  isOrderPage = false,
}: DomesticRatePlanOptionParams & { isOrderPage?: boolean }): string[] => {
  const { isAvailableParking } = getRatePlanParkingInfo(config)
  const { checkIn, checkOut, breakfast } = getDomesticRateplanBenefits(benefitList)
  return [breakfast, checkIn, checkOut, isAvailableParking || isOrderPage ? '' : '주차불가'].filter(
    isTruthy
  )
}
